import React from 'react';
import {
  useNotifications, FormHandlers, useModal, Action, Portal,
} from '@doveit/bricks';
import { JobApplication, Prospect, Rumor } from '../../../providers/api/dtos';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import UpdateAssignedAgentForm, { UpdateAssignedAgentFormModel } from '../../components/update-assigned-agent-form/UpdateAssignedAgentForm';

export const UPDATE_ASSIGNED_AGENT_SUCCESS_MESSAGE = "L'agente incaricato è stato sostituito con successo";
export const UPDATE_ASSIGNED_AGENT_ERROR_MESSAGE = "Non è stato possibile sostituire l'agente incaricato";

interface ChildFnProps {
  openUpdateModal: VoidFunction,
  isSaving: boolean,
}

export interface UpdateAssignedAgentActionProps<T> extends React.AriaAttributes {
  reference: T,
  getAgentReference: (ref: T) => string | number | undefined,
  onUpdate: (values: UpdateAssignedAgentFormModel) => Promise<T>,
  children?: (props: ChildFnProps) => React.ReactNode,
  onSuccess?: (reference: T) => void,
  isRequired?: boolean,
  label?: string,
}

function UpdateAssignedAgentAction<T extends Prospect | Rumor | JobApplication>({
  reference,
  label,
  getAgentReference,
  children,
  onUpdate,
  onSuccess,
  isRequired = false,
  'aria-label': ariaLabel,
  ...rest
}: UpdateAssignedAgentActionProps<T>) {
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const agentReference = React.useMemo(
    () => getAgentReference(reference),
    [reference, getAgentReference],
  );

  const { data: agent } = useAgent(agentReference);

  const handleSubmit = React.useCallback(async (values: UpdateAssignedAgentFormModel) => {
    try {
      setIsSaving(true);

      const updatedReference = await onUpdate(values);

      setIsSaving(false);
      modal.close();
      addSuccess(UPDATE_ASSIGNED_AGENT_SUCCESS_MESSAGE);

      onSuccess?.(updatedReference);
    } catch (err) {
      setIsSaving(false);
      addError(UPDATE_ASSIGNED_AGENT_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, modal, onSuccess, onUpdate]);

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  return (
    <>
      {children ? children({
        openUpdateModal: modal.open,
        isSaving,
      }) : (
        <Action
          label={label || 'Sostituisci agente incaricato'}
          aria-label={ariaLabel || "Azione per sostituire l'agente incaricato"}
          size="S"
          loading={isSaving}
          onClick={() => modal.open()}
          {...rest}
        />
      )}
      <Portal>
        <SimpleModal
          {...modal}
          title={`Sostituisci ${(label || 'agente incaricato').toLocaleLowerCase()}`}
          aria-label="Modale per sostituire l'agente"
          footer={(
            <Action
              label="Sostituisci"
              color="primary"
              emphasis="high"
              aria-label="Azione per confermare la sostituzione dell'agente"
              onClick={submitForm}
            />
          )}
        >
          <UpdateAssignedAgentForm
            label={label}
            initialValues={{ assignee: agent }}
            isRequired={isRequired}
            onSubmit={handleSubmit}
            innerRef={formRef}
          />
        </SimpleModal>
      </Portal>
    </>
  );
}

export default UpdateAssignedAgentAction;
